import {
  handleExecuteNativeFetch,
  DesiredExecutionProps,
  getAuthToken,
} from 'src/utilities/network-utility';
import { performWrappedFetch } from 'src/utilities/wrapped-fetch';
import { appConfigFactory } from '../factories/appConfig.factory';

export type ApiType = 'standard' | 'internalTools';

/**
 * NativeApiClient - A service for making authenticated API requests
 *
 * Supports both standard API (jjApiUrl) and internal tools API (itJJApiUrl).
 *
 * @example
 * ```typescript
 * // Internal Tools API client
 * const internalClient = new NativeApiClient(undefined, 'internalTools');
 * await internalClient.post('/privacycontrol/email_optout', { email });
 *
 * // Or use the exported singletons:
 * import { apiClient, internalToolsClient } from './NativeApiClient';
 * ```
 */
export class NativeApiClient {
  private appConfig: AppConfig;
  private baseUrl: string;
  private isCookieAuthSupported?: boolean;

  /**
   * @param config - App configuration (optional, uses factory default)
   * @param apiType - 'standard' for jjApiUrl (default) or 'internalTools' for itJJApiUrl
   */
  constructor(config?: AppConfig, apiType: ApiType = 'standard') {
    this.appConfig = config || appConfigFactory();
    this.baseUrl =
      apiType === 'internalTools'
        ? this.appConfig.itJJApiUrl
        : this.appConfig.jjApiUrl;
  }

  /**
   * Detects if the client environment supports cookie-based authentication
   * by making a test request to /session endpoint with credentials
   * @private
   * @returns Promise<boolean> - true if cookies are supported, false otherwise
   */
  private async detectCookieAuthSupport(): Promise<boolean> {
    try {
      await performWrappedFetch({
        method: 'GET',
        url: this.buildUrl('/session'),
        useCookieAuth: true,
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Builds the httpServiceData object required by handleExecuteNativeFetch
   * Important: Always uses jjApiUrl for auth, even for internal tools API
   * @private
   */
  private buildHttpServiceData() {
    return {
      apiUrl: this.appConfig.jjApiUrl, // Always use standard API for auth
      authToken: getAuthToken(),
      brand: this.appConfig.brand,
      clientId: this.appConfig.clientId,
    };
  }

  /**
   * Builds the full URL by combining base API URL with the endpoint path
   * Uses this.baseUrl which could be jjApiUrl or itJJApiUrl
   * @private
   */
  private buildUrl(endpoint: string): string {
    // Remove leading slash if present to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/')
      ? endpoint.slice(1)
      : endpoint;
    return `${this.baseUrl}/${cleanEndpoint}`;
  }

  /**
   * Makes an authenticated API request
   * @private
   */
  private async request<T = any>(
    method: DesiredExecutionProps['method'],
    endpoint: string,
    data?: any,
    headers?: Record<string, string>,
  ): Promise<T> {
    // Detect cookie auth support on first request
    if (this.isCookieAuthSupported === undefined) {
      this.isCookieAuthSupported = await this.detectCookieAuthSupport();
    }

    if (headers) {
      // If custom headers are provided, use performWrappedFetch directly with auth token
      const authToken = getAuthToken();
      return performWrappedFetch({
        method,
        url: this.buildUrl(endpoint),
        headers: {
          Authorization: `Bearer ${authToken}`,
          ...headers,
        },
        useCookieAuth: this.isCookieAuthSupported,
        data,
      });
    }

    return handleExecuteNativeFetch({
      httpServiceData: this.buildHttpServiceData(),
      method,
      url: this.buildUrl(endpoint),
      useCookieAuth: this.isCookieAuthSupported,
      data,
    });
  }

  /**
   * Makes an unauthenticated API request (bypasses auth flow entirely)
   * Use this for endpoints that don't require any authentication (e.g., /auth login)
   * @private
   */
  private async requestUnauthed<T = any>(
    method: DesiredExecutionProps['method'],
    endpoint: string,
    data?: any,
  ): Promise<T> {
    return performWrappedFetch({
      method,
      url: this.buildUrl(endpoint),
      data,
    });
  }

  /**
   * Makes a GET request
   * @param endpoint - API endpoint path (e.g., '/restaurants/123')
   * @param headers - Optional headers to include in the request
   */
  public async get<T = any>(
    endpoint: string,
    headers?: Record<string, string>,
  ): Promise<T> {
    return this.request<T>('GET', endpoint, undefined, headers);
  }

  /**
   * Makes a POST request
   * @param endpoint - API endpoint path (e.g., '/auth')
   * @param data - Request payload
   * @param headers - Optional headers to include in the request
   */
  public async post<T = any>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>,
  ): Promise<T> {
    return this.request<T>('POST', endpoint, data, headers);
  }

  /**
   * Makes a PUT request
   * @param endpoint - API endpoint path (e.g., '/subscription')
   * @param data - Request payload
   * @param headers - Optional headers to include in the request
   */
  public async put<T = any>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>,
  ): Promise<T> {
    return this.request<T>('PUT', endpoint, data, headers);
  }

  /**
   * Makes a DELETE request
   * @param endpoint - API endpoint path (e.g., '/subscription/123')
   * @param data - Optional request payload
   * @param headers - Optional headers to include in the request
   */
  public async delete<T = any>(
    endpoint: string,
    data?: any,
    headers?: Record<string, string>,
  ): Promise<T> {
    return this.request<T>('DELETE', endpoint, data, headers);
  }

  /**
   * Makes an unauthenticated POST request
   * Use this for endpoints that don't require authentication, such as /auth login
   * This bypasses the auth flow entirely and makes a direct API call
   * @param endpoint - API endpoint path (e.g., '/auth')
   * @param data - Request payload
   */
  public async postUnauthed<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.requestUnauthed<T>('POST', endpoint, data);
  }

  /**
   * Gets the current app configuration
   */
  public getConfig(): AppConfig {
    return this.appConfig;
  }

  /**
   * Updates the app configuration
   * @param config - Partial config to merge with existing config
   */
  public updateConfig(config: Partial<AppConfig>): void {
    this.appConfig = { ...this.appConfig, ...config };
  }
}

/**
 * Singleton instances for convenient access throughout the app
 */
export const apiClient = new NativeApiClient();
export const internalToolsClient = new NativeApiClient(
  undefined,
  'internalTools',
);
